import { BaseResponse } from "utils/Http";

export type JudgmentsIntegrationPageViewModel = {
  judgmentIntegrations: JudgmentIntegration[];
  activeJudgmentIntegration: JudgmentIntegration;
  userID: number;
  casePageUrl: string;
};

export type JudgmentIntegration = {
  id: number;
  name: string;
  creationDate?: Date;
  cases?: JudgmentIntegrationCase[];
  civilEntries?: JudgmentIntegrationCivilEntry[];
  aliasEntries?: JudgmentAlias[];
  lastUpdated?: Date;
  lastUpdateBy?: string;
  startDate?: Date;
  endDate?: Date;
  civilEntriesFileName?: string;
  aliasFileName?: string;
  creationUserID: number;
  creationDateFormatted: string;
  lastUpdatedFormatted: string;
  statusID: ProcessStatus;
  statusName: string;
  integrationCategoryID: IntegrationCategory;
  integrationCategoryName: string;
  canDeleteJudgmentIntegration?: boolean;
};

export type JudgmentIntegrationCase = {
  id: number;
  caseNumber: string;
  judgmentIntegrationID: number;
  judgmentIntegrationCivilEntryID: number;
  defendants: JudgmentIntegrationContact[];
  judgments: JudgmentIntegrationJudgment[];
  primaryDefendant: JudgmentIntegrationContact;
  civilCaseID: number;
  civilCaseNumber: string;
  caseSuccessfullyCreated?: boolean;
  error: string;
  status: IntegrationCaseStatuses;
  statusMessage: string;
};

export type JudgmentIntegrationContact = {
  ID: number;
  firstName: string;
  middleName: string;
  lastName: string;
  suffixName: string;
  fullName: string;
  judgmentIntegrationCaseID: number;
  address: JudgmentIntegrationAddress;
  isPrimary: boolean;
};

export type JudgmentIntegrationAddress = {
  ID: number;
  address1: string;
  address2: string;
  city: string;
  state: string;
  postalCode: string;
  fullAddress: string;
  judgmentIntegrationContactID: number;
};

export type JudgmentIntegrationJudgment = {
  ID: number;
  judgmentIntegrationCaseID: number;
  judgmentIntegrationContactID: number;
  filingDate: Date;
  amount: number;
  operation: JudgmentOperation;
  civilJudgmentID?: number;
  civilDocketEventID?: number;
};

export enum JudgmentOperation {
  Create = 0,
  Delete = 2,
}

export type JudgmentIntegrationCivilEntry = {
  ID: number;
  caseNumber: number;
  firstName?: string;
  lastName?: string;
  middleName?: string;
  suffix?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  postalCodeExtension?: string;
  balance: number;
  civilJudgmentFilingDate: Date;
  totalObligation: number;
  operation: JudgmentOperation;
  isProperlyFormatted: boolean;
  message: string;
  lineNumber: number;
  fileName: string;
  judgmentIntegrationID: number;
};

export type JudgmentAlias = {
  ID: number;
  caseNumber: number;
  firstName: string;
  lastName: string;
  middleName: string;
  suffix: string;
  isProperlyFormatted: boolean;
  message: string;
  lineNumber: number;
  fileName: string;
  judgmentIntegrationID: number;
};

export type CSFile = {
  fileName: string;
  fileContents?: string;
  fileExtension?: string;
  fileSizeMB: number;
};

export type JudgmentIntegrationImportCommand = {
  judgmentsFile?: CSFile;
  aliasesFile?: CSFile;
  judgmentIntegrationName: string;
  integrationCategoryID?: number;
};

export type ExternalJudgmentImportCommand = {
  judgmentIntegrationName: string;
  integrationCategoryID?: number;
  startDate?: Date;
  endDate?: Date;
  startDateString?: string;
  startTimeString?: string;
  endDateString?: string;
  endTimeString?: string;
  canDeleteJudgmentIntegration?: boolean;
};

export type JudgmentIntegrationImportResponse = BaseResponse & {
  judgmentIntegration: JudgmentIntegration;
};

export type ExternalJudgmentImportResponse = BaseResponse & {
  judgmentIntegration: JudgmentIntegration;
};

export type SaveJudgmentIntegrationCommand = {
  judgmentIntegration: JudgmentIntegration;
};

export type SaveJudgmentIntegrationResponse = BaseResponse & {
  judgmentIntegration: JudgmentIntegration;
};

export type DeleteJudgmentIntegrationCommand = {
  judgmentIntegrationId: number;
};

export type DeleteJudgmentIntegrationResponse = BaseResponse & {
  judgmentIntegrationId: number;
};

export type GetJudgmentIntegrationByIdCommand = {
  judgmentIntegrationID: number;
};

export type GetJudgmentIntegrationByIdResponse = BaseResponse & {
  judgmentIntegration: JudgmentIntegration;
};

export type GetAllJudgementIntegrationsResponse = BaseResponse & {
  judgmentIntegrations: JudgmentIntegration[];
};

export type JudgmentTableProps = {
  casePageUrl: string;
  showToast: (
    severityValue: any,
    summaryValue: string,
    detailsValue?: string,
    lifetime?: number,
    sticky?: boolean
  ) => void;
  showImportErrorMessage: (show: boolean) => void;
};

export type ImportFormProps = {
  showToast: (
    severityValue: any,
    summaryValue: string,
    detailsValue?: string,
    lifetime?: number,
    sticky?: boolean
  ) => void;
};

export type IntegrationImportsProps = {
  showToast: (
    severityValue: any,
    summaryValue: string,
    detailsValue?: string,
    lifetime?: number,
    sticky?: boolean
  ) => void;
};

export type ParsingResultsProps = {};

export type JudgmentIntegrationTransposerCommand = {
  judgmentIntegrationID?: number;
  judgmentIntegrationCaseID?: number;
  userID?: number;
};

export type SatisfactionCaseCreateRequestModel = {
  JudgmentIntegrationID?: number;
  JudgmentIntegrationCaseId?: number;
};
export type JudgmentIntegrationTransposerResponse = BaseResponse & {
  judgmentIntegration: JudgmentIntegration;
};

export type GenerateNoticeCommand = {
  judgmentIntegrationID?: number;
  judgmentIntegrationCaseID?: number;
  noticeReportName: string;
  praecipeReportName: string;
  caseNumber: string;
};

export type GenerateNoticeResponse = BaseResponse & {
  fileName: string;
  error: string;
};

export type GenerateDocketEventsCommand = {
  judgmentIntegrationID?: number;
  judgmentIntegrationCaseID?: number;
  noticeReportName: string;
  praecipeReportName: string;
  noticeCaseEventCategoryApplicationSettingName: string;
  praecipeCaseEventCategoryApplicationSettingName: string;
};

export type GenerateDocketEventsResponse = BaseResponse & {
  error: string;
  integration: JudgmentIntegration;
};

export type SatisfactionsTransposerCommand = {
  integrationID?: number;
  userID: number;
  jintCaseID?: number;
};

export type SatisfactionsTransposerResponse = BaseResponse & {
  error: string;
  judgmentIntegration: JudgmentIntegration;
};

export enum ProcessStatus {
  Imported = 1,
  CasesCreated = 2,
  DocketsCreated = 3,
  SatisfactionsCreated = 4,
}

export enum IntegrationCategory {
  Judgment = 1,
  Satisfaction = 2,
}

export enum IntegrationCaseStatuses {
  All = 0,
  Pending = 1,
  Created = 2,
  CaseNotFound = 3,
  JudgmentNotFound = 4,
  DebtorNotFound = 5,
  CaseCreationError = 6,
  DocketCreated = 7,
  CaseFound = 8,
  PrimaryContactNotFound = 9,
  MultiplePrimaryDefContactsFound = 10,
  MultipleCasesFound = 11,
  MultipleJudgmentsFound = 12,
}
